// Single source of truth for the Services detail pages (Services.js) and their
// tabs. Each tab has its OWN indexable URL — /<category>/<slug> — so Google and
// AI search can rank e.g. SAP IBP on its own page instead of all tabs sharing
// one URL behind ?tab=. Used by Services.js, App.js redirects, Header and Home.
//
// Categories with ONE tab keep their plain category URL (no slug).
// Old links (/<category> and /<category>?tab=<id>) still work: Services.js
// redirects them to the tab's URL (see serviceTabRedirect below).
//
// `seo` is the per-tab <title> / meta description. Titles are keyword-first and
// kept under ~60 characters so Google doesn't cut them off.

export const SERVICE_CATEGORIES = {
  "/digital-core-transformation": {
    defaultTab: "sap",
    tabs: [
      {
        id: "sap",
        slug: "sap-s4hana",
        name: "SAP S/4HANA Implementation & Migration",
        seo: {
          title: "SAP S/4HANA Implementation & Migration Services | Bestowal",
          description:
            "Greenfield, brownfield and hybrid SAP S/4HANA implementation and migration by senior SAP architects, with SAP BTP, SAC and MDG integration and post-go-live support.",
        },
      },
      {
        id: "ibp",
        slug: "sap-ibp",
        name: "SAP Integrated Business Planning (IBP)",
        seo: {
          title: "SAP IBP Implementation & Support Partner | Bestowal",
          description:
            "Certified SAP IBP consultants for demand, supply and S&OP planning. 18+ SAP IBP projects delivered across industries, with ongoing support and custom planning workflows.",
        },
      },
    ],
  },
  "/intelligent-industry-solutions": {
    defaultTab: "industry",
    tabs: [
      {
        id: "industry",
        slug: "industry-4-0-5-0",
        name: "Industry 4.0 / 5.0 Solutions",
        seo: {
          title: "Industry 4.0 & 5.0 Smart Factory Solutions | Bestowal",
          description:
            "Industry 4.0 and 5.0 solutions: smart factories, digital twins, predictive analytics and collaborative robotics, integrated with SAP, PLCs, SCADA and cloud platforms.",
        },
      },
      {
        id: "aiml",
        slug: "ai-ml",
        name: "AI / ML Solutions",
        seo: {
          title: "AI & Machine Learning Solutions | Bestowal Systems",
          description:
            "AI and machine learning solutions that sharpen decisions, cut operating costs through automation and personalise customer experiences, from predictive maintenance to generative AI.",
        },
      },
    ],
  },
  "/data-cloud-transformation": {
    defaultTab: "data",
    tabs: [
      {
        id: "data",
        slug: "data-engineering",
        name: "Data Engineering Services",
        seo: {
          title: "Data Engineering & Analytics Services | Bestowal Systems",
          description:
            "Data engineering services: data lake and data warehouse implementation, structured and unstructured data pipelines, analytics, and governance aligned with GDPR, HIPAA and SOC 2.",
        },
      },
      {
        id: "cloud",
        slug: "cloud-transformation",
        name: "Cloud Transformation Services",
        seo: {
          title: "Cloud Migration & Transformation Services | Bestowal",
          description:
            "Cloud migration and modernisation: assessment and roadmaps, workload prioritisation, automated secure transitions, and hybrid and multi-cloud setups without vendor lock-in.",
        },
      },
    ],
  },
  "/digital-experience-engagement": {
    defaultTab: "web",
    tabs: [
      {
        id: "web",
        slug: "web-development",
        name: "Custom Web Development",
        seo: {
          title: "Custom Web Development Services | Bestowal Systems",
          description:
            "Custom web development from discovery and mobile-first UI/UX design to agile delivery, testing, hosting, analytics integration and ongoing support.",
        },
      },
      {
        id: "mobile",
        slug: "mobile-app-development",
        name: "Mobile App Development",
        seo: {
          title: "Mobile App Development Services | Bestowal Systems",
          description:
            "Native and cross-platform mobile app development: strategy, UX design, agile MVP delivery, API integration and App Store / Play Store launch.",
        },
      },
    ],
  },
  "/product-engineering-innovation": {
    defaultTab: "product",
    tabs: [
      {
        id: "product",
        slug: null,
        name: "Product Engineering",
        seo: {
          title: "Product Engineering & MVP Development | Bestowal Systems",
          description:
            "End-to-end product engineering: discovery, UX prototyping, cloud-native development, QA automation, MVP launch and legacy modernisation into API-first platforms.",
        },
      },
    ],
  },
  "/flexible-talent-delivery-models": {
    defaultTab: "staff",
    tabs: [
      {
        id: "staff",
        slug: null,
        name: "IT Staff Augmentation",
        seo: {
          title: "IT Staff Augmentation Services | Bestowal Systems",
          description:
            "IT staff augmentation with skilled SAP, web, mobile, cloud, AI/ML and data professionals on short-term, long-term or project contracts, onsite or remote.",
        },
      },
    ],
  },
};

// Public URL of one tab, e.g. ("/digital-core-transformation", "ibp")
// → "/digital-core-transformation/sap-ibp". Single-tab categories → category URL.
export const serviceTabPath = (categoryPath, tabId) => {
  const tab = SERVICE_CATEGORIES[categoryPath]?.tabs.find((t) => t.id === tabId);
  return tab?.slug ? `${categoryPath}/${tab.slug}` : categoryPath;
};

// Splits a pathname into its category + tab. Matching is case-insensitive like
// React Router's. Returns null when the path isn't a Services page.
//   tab === undefined → the bare category URL (/digital-core-transformation)
//   tab === null      → an unknown slug (→ 404)
export const parseServicePath = (pathname) => {
  const clean = pathname.replace(/\/+$/, "").toLowerCase();
  for (const [categoryPath, category] of Object.entries(SERVICE_CATEGORIES)) {
    if (clean === categoryPath) return { categoryPath, category, tab: undefined };
    if (clean.startsWith(`${categoryPath}/`)) {
      const slug = clean.slice(categoryPath.length + 1);
      const tab = category.tabs.find((t) => t.slug && t.slug === slug) || null;
      return { categoryPath, category, tab };
    }
  }
  return null;
};

// Where an old-style link should go: /<category>?tab=<id> (or no ?tab=) → the
// tab's own URL. Returns null when no redirect is needed.
export const serviceTabRedirect = (categoryPath, search) => {
  const category = SERVICE_CATEGORIES[categoryPath];
  if (!category || category.tabs.length < 2) return null;
  const wanted = new URLSearchParams(search).get("tab");
  const tabId = category.tabs.some((t) => t.id === wanted) ? wanted : category.defaultTab;
  return serviceTabPath(categoryPath, tabId);
};
